import { computed, getCurrentInstance, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { useUserFlagsStore } from '@/store/userFlags'
import { useOnetrustStore } from '@/store/onetrust'
import { useSurveyStore } from '@/store/survey'
import { usePaywallStore } from '@/store/paywall'

import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import Jet from '@/modules/Jet'
import { useRoute } from 'vue-router'

export function useOnetrust() {
	const proxy = getCurrentInstance().proxy
	const route = useRoute()

	const mainStore = useMainStore()
	const locationStore = useLocationStore()
	const onetrustStore = useOnetrustStore()
	const surveyStore = useSurveyStore()

	const { getCountryCode: countryCode, getIsEUCountry: isEU } = storeToRefs(mainStore)
	const { getQueryParam: getQueryParamRef } = storeToRefs(locationStore)
	const {
		getActiveGroups: onetrustActiveGroups,
		isBannerAutoClosable: isOnetrustBannerAutoClosable,
		getOptInModel: optInModel,
		bannerClosed,
	} = storeToRefs(onetrustStore)

	const getQueryParam = (key) => getQueryParamRef.value(key)

	const isOneTrustDisabled = computed(() => {
		return ENV_DEV_OR_STAGING && getQueryParam('onetrust') === false
	})

	function setOnetrustData() {
		const cookieSettingsButton = document.getElementById('onetrust-pc-btn-handler')
		const onetrustPolicyText = document.getElementById('onetrust-policy-text')

		if (cookieSettingsButton && onetrustPolicyText) {
			onetrustPolicyText.appendChild(cookieSettingsButton)
		}

		const domainData = window.OneTrust.GetDomainData?.() || {}
		const activeGroups = window.OnetrustActiveGroups.split(',').filter(Boolean) || []
		const bannerClosedVal = window.OneTrust.IsAlertBoxClosed()
		const optInModelVal = domainData.ConsentModel?.Name === 'opt-in'

		onetrustStore.setActiveGroups(activeGroups)
		onetrustStore.setBannerClosed(bannerClosedVal)
		onetrustStore.setOptInModel(optInModelVal)
		onetrustStore.setInited(true)

		if (!bannerClosedVal) {
			sendOnetrustAnalytics(optInModelVal)
		}

		subscribeToOnetrustEvents(optInModelVal)

		if (isOneTrustDisabled.value && !bannerClosed.value) {
			window.OneTrust.AllowAll()
		}
	}

	function sendOnetrustAnalytics(optInModelVal) {
		if (optInModelVal) {
			proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_POPUP' })
			Jet.logUxEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_POPUP' })
		} else {
			proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_GENERAL_POPUP' })
			Jet.logUxEvent('PopupView', { title: 'COOKIE_CONSENT_GENERAL_POPUP' })
		}
	}

	function subscribeToOnetrustEvents(optInModelVal) {
		window.OneTrust.OnConsentChanged((event) => {
			onetrustStore.setActiveGroups(event.detail)
			onetrustStore.setBannerClosed(window.OneTrust.IsAlertBoxClosed())

			setCookiesRelatedConsents()

			proxy.$analytic.setUserProperties({
				CookieConsentActiveGroups: onetrustActiveGroups.value,
			})
		})

		document.getElementById('onetrust-consent-sdk').addEventListener(
			'click',
			(e) => {
				const clickTargetId = e.target.id || e.target.className

				if (optInModelVal) {
					if (clickTargetId === 'onetrust-accept-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_CONFIRM_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_CONFIRM_BUTTON' })

						sendOnetrustDoneEvent()
					} else if (clickTargetId === 'onetrust-pc-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_BUTTON' })

						proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
						Jet.logUxEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
					} else if (
						clickTargetId === 'accept-recommended-btn-handler' ||
						e.target.classList.contains('ot-pc-refuse-all-handler') ||
						e.target.classList.contains('save-preference-btn-handler')
					) {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CONFIRM_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CONFIRM_BUTTON' })

						sendOnetrustDoneEvent()
					} else if (clickTargetId === 'close-pc-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CLOSE_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CLOSE_BUTTON' })

						if (ENV_DEV_OR_STAGING) {
							const paywallStore = usePaywallStore()
							paywallStore.setCountdownFinishedAt(null)
							paywallStore.toggleCountdown(true)
							paywallStore.setRemoveDiscountAfterCountdown(null)
							useUserFlagsStore().updateLocalFlags({
								applyDiscountOnAbort: false,
								scratchCardPassed: false,
								scratchCardDiscountApplied: false,
							})
						}
					} else if (clickTargetId === 'ot-floating-button__open') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_WIDGET_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_WIDGET_BUTTON' })

						proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
						Jet.logUxEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
					}
				} else {
					if (clickTargetId === 'onetrust-accept-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_GENERAL_CONFIRM_BUTTON' })
						Jet.logUxEvent('ButtonClick', { title: 'COOKIE_CONSENT_GENERAL_CONFIRM_BUTTON' })

						sendOnetrustDoneEvent()
					}
				}
			},
			true,
		)
	}

	function sendOnetrustDoneEvent(userConsent = true) {
		setTimeout(() => {
			const bannerType = optInModel.value ? 'extended' : 'general'

			proxy.$analytic.logEvent('CookieConsentDone', {
				banner_type: bannerType,
				user_consent: Boolean(userConsent),
				cookie_type: onetrustActiveGroups.value,
			})

			Jet.logLogicalEvent('CookieConsentDone', {
				bannerType: bannerType,
				userConsent: Boolean(userConsent),
				cookieType: onetrustActiveGroups.value,
			})
		}, 500)
	}

	function setCookiesRelatedConsents() {
		if (countryCode.value === 'us') {
			if (onetrustActiveGroups.value.includes('targeting_cookies')) {
				surveyStore.addUserConsent('pd_marketing_purposes_us')
			} else {
				surveyStore.removeUserConsent('pd_marketing_purposes_us')
			}
		} else if (isEU.value || countryCode.value === 'uk') {
			if (onetrustActiveGroups.value.includes('targeting_cookies')) {
				surveyStore.addUserConsent('pd_marketing_purposes_eu-uk')
			} else {
				surveyStore.removeUserConsent('pd_marketing_purposes_eu-uk')
			}
		}
	}

	// created: import onetrust styles
	import(/* webpackChunkName: "onetrust-styles" */ '@/styles/onetrust.scss')

	// Watch $route changes
	watch(route, () => {
		if (!isOnetrustBannerAutoClosable.value) {
			return
		}

		if (!route.meta.isLanding || route.query.step) {
			window.OneTrust?.AllowAll?.()
			sendOnetrustDoneEvent(false)
		}
	})

	// mounted
	onMounted(() => {
		if (window.OneTrust?.GetDomainData().IsBannerLoaded) {
			setOnetrustData()
		} else {
			document.addEventListener('OneTrustReady', setOnetrustData, { once: true })
		}
	})
}
